import type { ChatProcessingStep } from '@/types/chat';
import { CheckIcon, DownChevronIcon, ProgressSpinner, RedErrorIcon } from '@repo/atomic-components';
import { ReactElement, useState } from 'react';
import { classNames as cn } from 'primereact/utils';

interface ProcessingStepsProps {
  steps: ChatProcessingStep[];
}

interface StepStatusIconProps {
  status: ChatProcessingStep['status'];
}

/** Spoken status, since the icons are the only thing carrying it visually. */
const STATUS_LABEL: Record<ChatProcessingStep['status'], string> = {
  completed: 'completed',
  failed: 'failed',
  in_progress: 'in progress',
};

const StepStatusIcon = ({ status }: StepStatusIconProps) => {
  const icons: Record<ChatProcessingStep['status'], ReactElement> = {
    completed: (
      <span className="[&_path]:!fill-theme-text-grey">
        <CheckIcon className="h-[16px] w-[16px]" />
      </span>
    ),
    failed: <RedErrorIcon className="h-[16px] w-[16px] text-red-500" />,
    in_progress: <ProgressSpinner unstyled style={{ width: 16, height: 16 }} strokeWidth="4" />,
  };

  // Icons are hidden from screen readers; the status is read as text.
  return (
    <>
      <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center">
        {icons[status]}
      </span>
      <span className="sr-only">{STATUS_LABEL[status]}</span>
    </>
  );
};

export const ProcessingSteps = ({ steps }: ProcessingStepsProps) => {
  // Collapsed by default, so steps don't push the streaming answer down.
  const [isOpen, setIsOpen] = useState(false);

  const isWorking = steps.some((s) => s.status === 'in_progress');
  const latestStep = steps[steps.length - 1];

  if (!latestStep) return null;

  return (
    <div className="mb-3 w-full max-w-150 px-3 py-2 text-sm">
      <button
        type="button"
        className="text-theme-text-grey flex w-full cursor-pointer items-center justify-start gap-3 text-left font-semibold"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
      >
        {isOpen ? (
          <span>{isWorking ? 'Working...' : 'Processing steps'}</span>
        ) : (
          // Collapsed, the most recent step stands in for the whole list — one row, so the
          // response below it stays in view.
          <>
            <StepStatusIcon status={latestStep.status} />
            <span className="line-clamp-2 min-w-0 font-normal">{latestStep.label}</span>
          </>
        )}
        <DownChevronIcon
          className={cn('h-[16px] w-[16px] shrink-0 transition-transform', isOpen ? 'rotate-180' : 'rotate-0')}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className="mt-2 flex max-h-[50vh] flex-col items-start gap-2 overflow-y-auto">
          {steps.map((s) => (
            <div key={s.id} className="text-theme-text-grey flex items-center gap-3">
              <StepStatusIcon status={s.status} />
              <div>{s.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
